import { MPOST } from 'common/helpers.ts';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWRMutation from 'swr/mutation';
import { Button } from 'ui/component/Button.tsx';
import { Modal } from 'ui/component/Modal.tsx';
import { Input } from 'ui/control/Input.tsx';
import { getTranslatedError } from '#admin/helpers.ts';

type Props = {
  onSave?: () => void;
  onClose?: () => void;
  isOpen: boolean;
};

type FormValues = {
  name: string;
  description: string;
};

export const RoleForm = ({ onSave, onClose, isOpen }: Props) => {
  const { t } = useTranslation();
  const mutation = useSWRMutation('/role', MPOST);
  const isLoading = mutation.isMutating;

  const formMethods = useForm<FormValues>({
    mode: 'onTouched',
    criteriaMode: 'all',
    shouldUnregister: false, // we need the default values to be applied in all cases
    shouldUseNativeValidation: false,
    shouldFocusError: true,
    defaultValues: {
      name: '',
      description: '',
    },
  });

  const { handleSubmit, formState, reset } = formMethods;

  const onSubmit = async (values: FormValues) => {
    await mutation.trigger(values);
    reset();
    if (onSave) onSave();
  };

  const handleClose = async () => {
    reset();
    if (onClose) onClose();
  };

  return (
    <Modal title={<h4>{t('forms.role.title')}</h4>} isOpen={isOpen} onClose={handleClose}>
      <FormProvider {...formMethods}>
        <form className="flex max-h-[550px] w-[450px] flex-col gap-2.5 overflow-y-scroll" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-2">
            <Input
              name="name"
              label={t('forms.role.name.label')}
              disabled={isLoading}
              registerOptions={{ required: true, validate: { uppercase: (value) => value === value.toUpperCase() } }}
            >
              {formState.errors.name?.type === 'required' && t('forms.role.name.required')}
              {formState.errors.name?.type === 'uppercase' && t('forms.role.name.uppercase')}
            </Input>
            <Input name="description" label={t('forms.role.description.label')} disabled={isLoading}>
              {formState.errors.name?.type === 'required' && t('forms.role.description.required')}
            </Input>
          </div>

          <div className="mt-4 flex gap-5">
            <Button type="button" disabled={isLoading} className="blue-outlined" onClick={handleClose}>
              {t('actions.back')}
            </Button>
            <Button type="submit" disabled={!formState.isValid} loading={isLoading} className="blue flex items-center justify-center">
              {t('actions.createRole')}
            </Button>
          </div>
          {mutation.error && <span className="text-center text-red-500">{getTranslatedError(mutation.error, t) || t('forms.role.error.save')}</span>}
        </form>
      </FormProvider>
    </Modal>
  );
};
